import { useNavigate } from 'react-router-dom';
import { Header, Footer } from '@/components/ui';
import AdminSidebar from '@/components/ui/AdminSidebar';
import ProfileAvatar from '@/components/ui/ProfileAvatar';
import { PanelHero } from '@/components/ui/panel';
import DeleteUserButton from '@/components/users/DeleteUser';
import { useUsers } from '@/context/UsersContext';
import { useState, useEffect } from 'react';

function RoleBadge({ role }: { role: string }) {
  const isAdmin = role === 'admin';
  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wide ${
        isAdmin ? 'bg-primary/10 text-primary ring-1 ring-primary/20' : 'bg-slate-100 text-secondary/70 ring-1 ring-slate-200'
      }`}
    >
      {role}
    </span>
  );
}

function StatusBadge({ active }: { active: boolean }) {
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${
        active ? 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200' : 'bg-slate-100 text-slate-500 ring-1 ring-slate-200'
      }`}
    >
      <span className={`h-1.5 w-1.5 rounded-full ${active ? 'bg-emerald-500' : 'bg-slate-400'}`} />
      {active ? 'Attivo' : 'Disabilitato'}
    </span>
  );
}

export default function UserSettingsPage() {
  const navigate = useNavigate();
  const { users, fetchUsers } = useUsers();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const adminCount = users.filter((u) => u.role === 'admin').length;
  const activeCount = users.filter((u) => u.is_active).length;

  return (
    <>
      <Header onAdminClick={() => setSidebarOpen(true)} />
      <AdminSidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white pb-12">
        <div className="mx-auto max-w-5xl px-4 py-6 md:px-6">
          <button
            type="button"
            onClick={() => navigate('/home')}
            className="mb-5 inline-flex items-center gap-2 rounded-lg px-2 py-1.5 text-sm font-semibold text-primary transition hover:bg-primary/5"
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
            Torna alla Home
          </button>

          <PanelHero
            eyebrow="Amministrazione"
            title="Gestione utenti"
            description="Gli utenti si sincronizzano dal login JEVIS. Ruolo e profilo arrivano da Kernel; qui puoi solo eliminare account."
          />

          <div className="mt-6 grid grid-cols-3 gap-3">
            {[
              { label: 'Totale', value: users.length },
              { label: 'Attivi', value: activeCount },
              { label: 'Admin', value: adminCount },
            ].map((stat) => (
              <div
                key={stat.label}
                className="rounded-2xl border border-slate-200/80 bg-white px-4 py-3 text-center shadow-sm"
              >
                <p className="text-2xl font-extrabold text-secondary">{stat.value}</p>
                <p className="text-xs font-medium uppercase tracking-wide text-gray-400">{stat.label}</p>
              </div>
            ))}
          </div>

          <div className="mt-8 space-y-4">
            {users.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-slate-300 bg-white px-6 py-16 text-center">
                <p className="text-lg font-semibold text-secondary">Nessun utente</p>
                <p className="mt-1 text-sm text-gray-500">
                  Gli account compaiono dopo il primo accesso con JEVIS.
                </p>
              </div>
            ) : (
              users.map((user) => (
                <article
                  key={user.id}
                  className="group rounded-2xl border border-slate-200/80 bg-white p-5 shadow-sm transition hover:border-secondary/20 hover:shadow-md md:flex md:items-center md:justify-between"
                >
                  <div className="flex items-start gap-4">
                    <ProfileAvatar
                      name={user.username}
                      src={user.profile_picture_url}
                      size="lg"
                      className="rounded-2xl"
                    />
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <h3 className="text-lg font-bold text-secondary">{user.username}</h3>
                        <StatusBadge active={user.is_active} />
                        <RoleBadge role={user.role} />
                      </div>
                      <p className="mt-1 truncate text-sm text-gray-500">{user.email || '—'}</p>
                      <p className="mt-2 text-xs text-gray-400">
                        Iscritto il {new Date(user.created_at).toLocaleDateString('it-IT')}
                      </p>
                    </div>
                  </div>

                  <div className="mt-4 flex gap-2 md:mt-0 md:shrink-0">
                    <DeleteUserButton userId={user.id} />
                  </div>
                </article>
              ))
            )}
          </div>
        </div>
      </div>

      <Footer />
    </>
  );
}
